import { toast } from '@/components/toast';
import { maxAudioSizeInBytes, projectsBucket } from '@/environment';
import { AudioData, useUploadAudio } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { useAnalytics } from '@/services/analytics';
import { DidUser, useAuth } from '@/services/auth';
import { annoynmousUserToEditorPagePostSsoReturnTo, renameFile } from '@/utils/utils';
import { nanoid } from 'nanoid';
import { useAdvancedEditorContext } from '../context/useAdvancedEditorContext';
import { useCurrentProjectId } from './useCurrentProjectId';

const MAX_DURATION = 5 * 60;
export const getAudioResultUrl = ({ user, projectId, pageId }: { projectId: string; user?: DidUser | null; pageId: string }) => {
    if (!pageId) {
        throw new Error('pageId is required');
    }

    const userPath = user?.org_id ? `${user.org_id}/${user.sub}` : user?.sub;
    return `${projectsBucket}/${userPath}/${projectId}/scenes/${pageId}/data/audio.wav`;
};

const toastOrCallback = (onUploadError?: (error: string) => void) => (error: string) => {
    if (onUploadError) {
        onUploadError(error);
    } else {
        toast.error({ title: error });
    }
};

export const useUploadAudioFile = (onUploadComplete?: (audioData: AudioData) => void, onUploadError?: (error: string) => void) => {
    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect } = useAuth();
    const projectId = useCurrentProjectId();
    const { user } = useAuth();
    const { store } = useAdvancedEditorContext();
    const { mutateAsync: uploadAudio } = useUploadAudio(onUploadComplete);

    const activePageId = store.activePage?.id;

    const errorHandler = toastOrCallback(onUploadError);

    return {
        isReady: !!activePageId,
        upload: async (file: File): Promise<void> => {
            if (isAnonymous) {
                mixpanel?.track('go-to-signup', { event: 'click', from: 'upload-audio', isAnonymous });
                loginWithRedirect({
                    appState: { returnTo: annoynmousUserToEditorPagePostSsoReturnTo },
                });
                return;
            }

            if (!file) return;

            if (!file.type.match(/^audio\/.*$|^video\/.*$/)) {
                errorHandler(`Invalid audio type: ${file.type}`);
            } else if (file.size > maxAudioSizeInBytes) {
                errorHandler(`Audio file must be under ${Math.floor(maxAudioSizeInBytes * 0.00001) / 10}MB`);
            } else {
                try {
                    const audio = new Audio();
                    audio.src = URL.createObjectURL(file);

                    await new Promise<void>(resolve => {
                        audio.addEventListener('loadedmetadata', () => {
                            resolve();
                        });
                    });

                    if (audio.duration > MAX_DURATION) {
                        errorHandler('Audio file should be up to 5 minutes long');
                        return;
                    }

                    await uploadAudio({
                        audio: renameFile(file, `audio_${nanoid()}`),
                        resultUrl: getAudioResultUrl({
                            user,
                            projectId,
                            pageId: activePageId,
                        }),
                    });

                    mixpanel?.track('audio-upload', { event: 'success' });
                } catch {
                    mixpanel?.track('audio-upload', { event: 'error' });
                }
            }
        },
    };
};
